// src/components/CustoCopiloto.js
// Seção "Copiloto em tempo real" da aba Custo (Documentação do Memor.IA).
// Mostra quanto o copiloto custou no mês: transcrição (Gemini Flash-Lite, por tokens de
// áudio, só nos trechos com fala) + sugestões da IA (Gemini), com projeção, custo por hora de reunião,
// ranking por cliente/operador e as últimas sessões. Dados de /api/reunioes/custo-copiloto/.
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const TIPO_LABEL = { comercial: 'Comercial', servico: 'Serviço', cs: 'CS', gestao: 'Gestão / 1:1', vision: 'Vision', reforma_tributaria: 'Reforma Tributária' };

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const mesCurto = (aaaamm) => {
  const [a, m] = (aaaamm || '').split('-');
  return m ? `${MESES[Number(m) - 1]}/${a.slice(2)}` : aaaamm;
};

const horasTexto = (h) => {
  const min = Math.round((h || 0) * 60);
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')}`;
};

export default function CustoCopiloto({ brl, cor, usdBrl }) {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('access');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/reunioes/custo-copiloto/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => { if (r.data.success) setDados(r.data); else setErro(r.data.erro || 'Não foi possível carregar.'); })
      .catch((e) => setErro(e.response?.data?.erro || 'Não foi possível carregar o custo do copiloto.'))
      .finally(() => setCarregando(false));
  }, []);

  const mes = dados?.mes;
  const totalMes = mes?.total_usd || 0;
  const pctStt = totalMes > 0 ? Math.round((mes.custo_stt_usd / totalMes) * 100) : 0;
  const maxHist = Math.max(0.0001, ...((dados?.historico || []).map((h) => h.total_usd)));

  return (
    <div className="card border-0 shadow-sm mb-4" style={{ borderLeft: `4px solid ${cor}` }}>
      <div className="card-body py-4">
        <h5 className="fw-bold mb-1" style={{ color: '#212529' }}>
          <i className="fas fa-headset me-2" style={{ color: cor }}></i>
          Copiloto em tempo real
        </h5>
        <p className="mb-4" style={{ color: '#6c757d', fontSize: '0.92rem', lineHeight: 1.6 }}>
          Custo das sessões do copiloto nas reuniões ao vivo. São duas partes: a <strong>transcrição</strong>, feita pelo
          Gemini Flash-Lite e cobrada pelos tokens de áudio, somente nos trechos em que alguém está falando nos
          {' '}<strong>dois canais</strong> (cliente e você — o silêncio não é enviado), e as <strong>sugestões</strong> da IA
          (Gemini), medidas pelos tokens de cada chamada. Sessões anteriores à mudança foram medidas com o preço do
          Google Speech-to-Text (US$ {(dados?.preco_stt_usd_min ?? 0.024).toFixed(3)}/min de áudio).
        </p>

        {carregando ? (
          <div className="text-center py-4">
            <div className="spinner-border" style={{ color: cor }}></div>
          </div>
        ) : erro ? (
          <div className="alert alert-warning mb-0" style={{ fontSize: '0.9rem' }}>
            <i className="fas fa-triangle-exclamation me-2"></i>{erro}
          </div>
        ) : !mes || mes.sessoes === 0 ? (
          <div className="text-center py-4" style={{ color: '#6c757d' }}>
            <i className="fas fa-headset fa-2x mb-2 d-block" style={{ opacity: 0.5 }}></i>
            Nenhuma sessão do copiloto registrada neste mês ainda — o custo aparece aqui a partir da próxima reunião.
          </div>
        ) : (
          <>
            {/* KPIs */}
            <div className="row g-3 mb-4">
              {[
                { label: 'Custo no mês', valor: brl(totalMes), sub: `US$ ${totalMes.toFixed(2)}`, icon: 'fa-coins' },
                { label: 'Projeção até o fim do mês', valor: brl(mes.projecao_mes_usd), sub: 'no ritmo atual', icon: 'fa-chart-line' },
                { label: 'Custo por hora de reunião', valor: brl(mes.custo_por_hora_usd), sub: `${horasTexto(mes.horas_reuniao)} no mês`, icon: 'fa-clock' },
                { label: 'Custo médio por sessão', valor: brl(mes.custo_medio_sessao_usd), sub: `${mes.sessoes} sessões · ${mes.sugestoes} sugestões`, icon: 'fa-headset' },
              ].map((k) => (
                <div className="col-6 col-lg-3" key={k.label}>
                  <div className="p-3 rounded h-100" style={{ background: '#f8f9fa' }}>
                    <div style={{ fontSize: '0.78rem', color: '#6c757d' }}>
                      <i className={`fas ${k.icon} me-1`} style={{ color: cor }}></i>{k.label}
                    </div>
                    <div className="fw-bold" style={{ fontSize: '1.35rem', color: '#212529' }}>{k.valor}</div>
                    <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{k.sub}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Divisão transcrição × IA */}
            <div className="mb-4">
              <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.85rem' }}>
                <span><i className="fas fa-microphone-lines me-1" style={{ color: '#0d6efd' }}></i>
                  Transcrição: <strong>{brl(mes.custo_stt_usd)}</strong> ({pctStt}%) · {Math.round(mes.minutos_audio)} min de áudio</span>
                <span><i className="fas fa-wand-magic-sparkles me-1" style={{ color: cor }}></i>
                  Sugestões IA: <strong>{brl(mes.custo_ia_usd)}</strong> ({100 - pctStt}%)</span>
              </div>
              <div className="d-flex rounded overflow-hidden" style={{ height: 10, background: '#e9ecef' }}>
                <div style={{ width: `${pctStt}%`, background: '#0d6efd' }}></div>
                <div style={{ width: `${100 - pctStt}%`, background: cor }}></div>
              </div>
            </div>

            <div className="row g-4 mb-4">
              {/* Histórico mensal */}
              <div className="col-lg-4">
                <h6 className="fw-bold mb-3" style={{ fontSize: '0.9rem' }}>Últimos meses</h6>
                {(dados.historico || []).slice().reverse().map((h) => (
                  <div key={h.mes} className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: '0.82rem' }}>
                    <span style={{ width: 48, color: '#6c757d' }}>{mesCurto(h.mes)}</span>
                    <div className="flex-grow-1 rounded" style={{ background: '#e9ecef', height: 8 }}>
                      <div className="rounded" style={{ width: `${Math.max(3, (h.total_usd / maxHist) * 100)}%`, height: 8, background: cor }}></div>
                    </div>
                    <span className="fw-semibold" style={{ width: 90, textAlign: 'right' }}>{brl(h.total_usd)}</span>
                  </div>
                ))}
              </div>

              {/* Por cliente e por operador */}
              {[
                { titulo: 'Por cliente (mês)', lista: dados.por_cliente || [], chave: 'cliente' },
                { titulo: 'Por operador (mês)', lista: dados.por_operador || [], chave: 'operador' },
              ].map((bloco) => (
                <div className="col-lg-4" key={bloco.titulo}>
                  <h6 className="fw-bold mb-3" style={{ fontSize: '0.9rem' }}>{bloco.titulo}</h6>
                  {bloco.lista.length === 0 ? (
                    <div style={{ fontSize: '0.82rem', color: '#6c757d' }}>—</div>
                  ) : bloco.lista.map((item) => (
                    <div key={item[bloco.chave]} className="d-flex justify-content-between border-bottom py-1" style={{ fontSize: '0.82rem' }}>
                      <span className="text-truncate me-2" title={item[bloco.chave]}>
                        {item[bloco.chave]}
                        <span style={{ color: '#6c757d' }}> · {item.sessoes}× · {horasTexto(item.horas)}</span>
                      </span>
                      <span className="fw-semibold text-nowrap">{brl(item.total_usd)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Últimas sessões */}
            <h6 className="fw-bold mb-2" style={{ fontSize: '0.9rem' }}>Últimas sessões</h6>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0" style={{ fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ color: '#6c757d' }}>
                    <th>Quando</th><th>Operador</th><th>Cliente</th><th>Tipo</th>
                    <th className="text-end">Duração</th><th className="text-end">Sugestões</th>
                    <th className="text-end">Transcrição</th><th className="text-end">IA</th><th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(dados.sessoes || []).map((s, i) => (
                    <tr key={i}>
                      <td className="text-nowrap">{s.data_hora}</td>
                      <td>{s.operador}</td>
                      <td>{s.cliente || '—'}</td>
                      <td>{TIPO_LABEL[s.tipo] || s.tipo || '—'}</td>
                      <td className="text-end">{s.minutos.toLocaleString('pt-BR')} min</td>
                      <td className="text-end">{s.sugestoes}</td>
                      <td className="text-end">{brl(s.custo_stt_usd)}</td>
                      <td className="text-end">{brl(s.custo_ia_usd)}</td>
                      <td className="text-end fw-semibold">{brl(s.total_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 mb-0" style={{ fontSize: '0.75rem', color: '#6c757d' }}>
              Valores em reais com câmbio aproximado de R$ {usdBrl.toFixed(2).replace('.', ',')}. O preço da transcrição não
              considera a faixa gratuita do Google — a conferência oficial é no faturamento do Google Cloud.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
